// Fetch real posted scans from other users for the Nearby feed.
// Joined client-side with each scan owner's profile so we can show handle/avatar.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBlockedUsers } from "@/hooks/useBlockedUsers";
import { type Coords, distanceMi } from "@/lib/geo";
import type { SwatchKind } from "@/components/primitives";
import { parseMarketplaceLinks, type MarketplaceLink } from "@/lib/marketplaces";
import { normalizeToMetro } from "@/lib/metros";

export interface NearbyScan {
  id: string;
  user_id: string;
  title: string;
  brand: string | null;
  era: string | null;
  category: string | null;
  tags: string[];
  caption: string | null;
  img: string;
  photos: string[];
  verdict: "PASS" | "FAIL";
  is_verified: boolean;
  confidence: number | null;
  price_suggested: number | null;
  lat: number;
  lng: number;
  city: string | null;
  created_at: string;
  posted_to: string | null;
  marketplace_links: MarketplaceLink[];
  sold_at: string | null;
  sold_price: number | null;
  status: string | null;
  verification: unknown;
  /** "manual" = quick-post (skip-just-post-it) that never ran through
   *  fig-verify. Used to suppress the VINTAGE / FAST FASH stamp on
   *  feed cards for items whose verdict column is a NOT-NULL placeholder. */
  post_kind: string | null;
  handle: string;
  display_name: string | null;
  avatar: SwatchKind;
  distanceMi: number | null;
}

/**
 * Nearby posted scans.
 *
 * - `originCity` is the primary filter when supplied. We compare on a
 *   METRO-NORMALIZED key (see lib/metros.ts), so "Brooklyn" matches
 *   "Bushwick" (both → "new york"), "Hollywood" matches "Santa Monica"
 *   (both → "los angeles"), etc. Falls back to lowercased first-segment
 *   for cities not in the alias table, so smaller cities still match
 *   on exact name.
 * - `originCoords` + `radiusMi` are kept as a secondary filter. When the
 *   user has no city set we fall back to coord-distance. We also use coords
 *   to compute distance labels for cards even when city is the primary
 *   filter (so users still see "12 mi" type strings).
 */

export function useNearbyScans(
  originCoords: Coords | null,
  radiusMi: number,
  originCity: string | null = null,
) {
  const { user } = useAuth();
  const { data: blockedSet } = useBlockedUsers();
  const [rows, setRows] = useState<NearbyScan[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      // Pull recent posted scans that have coordinates. RLS already restricts
      // the result to "posted = true OR own".
      const { data: scans, error: scansErr } = await (supabase as any)
        .from("scans_public")
        .select("id, user_id, title, brand, era, category, tags, caption, img, photos, verdict, is_verified, confidence, price_suggested, lat, lng, city, created_at, posted_to, marketplace_links, sold_at, sold_price, status, verification, post_kind")
        .eq("posted", true)
        .order("created_at", { ascending: false })
        .limit(200);

      if (cancelled) return;
      if (scansErr || !scans) {
        if (scansErr) console.warn("useNearbyScans:", scansErr.message);
        setRows([]);
        setLoading(false);
        return;
      }

      // Drop suspended scans. `scans_public` view doesn't expose
      // `suspended_at`, so we re-query the base table for IDs that are NOT
      // suspended and intersect. RLS still restricts non-owners to posted.
      const candidateIds = scans.map((s) => s.id);
      let safeIds = new Set<string>(candidateIds);
      if (candidateIds.length > 0) {
        const { data: ok } = await supabase
          .from("scans")
          .select("id")
          .in("id", candidateIds)
          .is("suspended_at", null);
        if (ok) safeIds = new Set(ok.map((r) => r.id));
      }
      // Hide my own scans from "Nearby" — those belong on /history.
      // Also drop suspended posts and content from blocked users.
      const others = scans
        .filter((s) => s.user_id !== user?.id)
        .filter((s) => safeIds.has(s.id))
        .filter((s) => !blockedSet?.has(s.user_id));
      const userIds = Array.from(new Set(others.map((s) => s.user_id)));
      if (userIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const { data: profiles } = await (supabase as any)
        .from("profiles_public")
        .select("id, handle, display_name, avatar")
        .in("id", userIds);

      if (cancelled) return;
      const profileById = new Map(
        ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );

      const enriched: NearbyScan[] = others.map((s) => {
        const prof = profileById.get(s.user_id);
        const sold = s as { sold_at?: string | null; sold_price?: number | null; status?: string | null };
        return {
          id: s.id,
          user_id: s.user_id,
          title: s.title,
          brand: s.brand,
          era: s.era,
          category: (s as { category?: string | null }).category ?? null,
          tags: (Array.isArray((s as { tags?: unknown }).tags) ? (s as { tags: string[] }).tags : []),
          caption: s.caption,
          img: s.img,
          photos: s.photos ?? [],
          verdict: s.verdict as "PASS" | "FAIL",
          is_verified: !!(s as { is_verified?: boolean }).is_verified || s.verdict === "PASS",
          confidence: s.confidence,
          price_suggested: s.price_suggested ? Number(s.price_suggested) : null,
          lat: Number(s.lat),
          lng: Number(s.lng),
          city: s.city,
          created_at: s.created_at,
          posted_to: (s as { posted_to?: string | null }).posted_to ?? null,
          marketplace_links: parseMarketplaceLinks(
            (s as { marketplace_links?: unknown }).marketplace_links,
          ),
          sold_at: sold.sold_at ?? null,
          sold_price: sold.sold_price != null ? Number(sold.sold_price) : null,
          status: sold.status ?? null,
          verification: (s as { verification?: unknown }).verification ?? null,
          post_kind: (s as { post_kind?: string | null }).post_kind ?? null,
          handle: prof?.handle ?? "collector",
          display_name: prof?.display_name ?? null,
          avatar: (prof?.avatar as SwatchKind) ?? "cream",
          distanceMi: originCoords
            ? distanceMi(originCoords, { lat: Number(s.lat), lng: Number(s.lng) })
            : null,
        };
      });

      setRows(enriched);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user?.id, originCoords?.lat, originCoords?.lng]);

  // Primary filter is metro key when available. Secondary filter is distance.
  // We sort by distance within the matched set so closer pieces lead the
  // feed even when many share the same metro.
  const inRadius = useMemo(() => {
    const targetMetro = normalizeToMetro(originCity);
    if (targetMetro) {
      return rows
        .filter((r) => normalizeToMetro(r.city) === targetMetro)
        .sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity));
    }
    if (originCoords) {
      return rows
        .filter((r) => r.distanceMi != null && r.distanceMi <= radiusMi)
        .sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity));
    }
    return rows;
  }, [rows, originCoords, originCity, radiusMi]);

  return { nearbyScans: inRadius, allPosted: rows, loading };
}
